/**
 * lib/pending-invite.ts
 *
 * The invitation link a child shares (TUP-1835): `/invite?code=ABCD1234`,
 * plus `&mode=here` when the child opened it on their own phone. A parent
 * may have to sign up, sign in or verify a phone OTP before they can use it,
 * and each of those navigates away, so the code is parked in localStorage
 * (24h, the same life as the code itself) and `/invite` reads it back.
 *
 * Storage can throw or be absent (private mode, blocked site data), so every
 * access is wrapped. A parent whose storage is unusable still gets a working
 * invite page as long as the URL keeps its `?code=`.
 */

import { PAIRING_CODE_LENGTH } from '@tupai/types';

export const PENDING_INVITE_KEY = 'tupai_parent_pending_invite';
export const PENDING_INVITE_TTL_MS = 24 * 60 * 60 * 1000;

export interface PendingInvite {
  code: string;
  /** 'here': the student opened the link on their own phone, so the parent
   *  is signed out again once linked. */
  mode?: 'here';
}

interface StoredInvite extends PendingInvite {
  expiresAt: number;
}

/** Pairing codes are upper-case letters and digits, exactly
 *  PAIRING_CODE_LENGTH long. Returns null for anything else. */
export function normaliseInviteCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return code.length === PAIRING_CODE_LENGTH ? code : null;
}

export function setPendingInvite(invite: PendingInvite, now: number = Date.now()): void {
  const stored: StoredInvite = {
    code: invite.code,
    ...(invite.mode ? { mode: invite.mode } : {}),
    expiresAt: now + PENDING_INVITE_TTL_MS,
  };
  try {
    localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable: the invite page falls back to the URL's code.
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/** The stored invite, or null when there is none, it expired, or the entry
 *  is malformed. An expired or malformed entry is removed on the way out. */
export function getPendingInvite(now: number = Date.now()): PendingInvite | null {
  try {
    const raw = localStorage.getItem(PENDING_INVITE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredInvite> | null;
    const code = normaliseInviteCode(parsed?.code);
    if (code && typeof parsed?.expiresAt === 'number' && parsed.expiresAt > now) {
      return { code, ...(parsed.mode === 'here' ? { mode: 'here' as const } : {}) };
    }
  } catch {
    // Fall through: malformed JSON is treated as no invite.
  }
  clearPendingInvite();
  return null;
}

export function hasPendingInvite(now: number = Date.now()): boolean {
  return getPendingInvite(now) !== null;
}
